import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  useVoice, 
  VOICE_PERSONALITIES, 
  VOICE_ACCENTS, 
  VOICE_GENDERS,
  VOICE_AGE_GROUPS,
  VOICE_PROVIDERS
} from '../../context/VoiceContext';
import { 
  Volume2, 
  VolumeX, 
  Play, 
  Pause, 
  Settings,
  User,
  Globe,
  Sliders,
  Check,
  Mic,
  Sparkles,
  Zap,
  Loader2
} from 'lucide-react';
import { GlassCard } from '../layout/GlassCard';

/**
 * AIRA VOCAL Settings Panel
 * 
 * Complete voice configuration UI for AIRA VOCAL engine:
 * - Pre-defined AIRA voice personas
 * - Custom voice settings (age, gender, accent)
 * - Speed, pitch, and volume controls
 * - AIRA VOCAL status indicator
 * - Test voice functionality
 */

const VoiceSettingsPanel = ({ variant = 'full' }) => {
  const {
    voiceEnabled,
    setVoiceEnabled,
    personality,
    setPersonality,
    accent,
    setAccent,
    gender,
    setGender,
    age,
    setAge,
    rate,
    setRate,
    pitch,
    setPitch,
    volume,
    setVolume,
    isSpeaking,
    airaVocalAvailable,
    airaVocalLoading,
    provider,
    setProvider,
    availableVoices,
    selectedVoiceIndex,
    setSelectedVoiceIndex,
    speak,
    stopSpeaking,
    testVoice,
    applyPersonality
  } = useVoice();

  const [activeTab, setActiveTab] = useState('personality');

  // Compact variant for quick settings
  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-3">
        <button
          onClick={() => setVoiceEnabled(!voiceEnabled)}
          className={`p-2 rounded-lg transition-all ${
            voiceEnabled 
              ? 'bg-[var(--color-primary)] text-white' 
              : 'bg-[var(--bg-card)] text-[var(--text-secondary)]'
          }`}
          title={voiceEnabled ? 'Voice enabled' : 'Voice disabled'}
        >
          {voiceEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
        </button>
        
        {voiceEnabled && (
          <>
            <select
              value={personality}
              onChange={(e) => applyPersonality(e.target.value)}
              className="input-field py-2 px-3 text-sm"
            >
              {Object.values(VOICE_PERSONALITIES).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            
            <button
              onClick={isSpeaking ? stopSpeaking : testVoice}
              className="p-2 bg-[var(--bg-card)] rounded-lg hover:bg-[var(--bg-card-hover)] transition-colors"
              title="Test voice"
            >
              {isSpeaking ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
          </>
        )}
      </div>
    );
  }

  // Full settings panel
  return (
    <div className="space-y-6" data-testid="voice-settings-panel">
      {/* Voice Enable Toggle */}
      <div className="flex items-center justify-between p-4 bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)]">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            voiceEnabled ? 'bg-[var(--color-primary)]' : 'bg-[var(--bg-tertiary)]'
          }`}>
            {voiceEnabled ? (
              <Volume2 className="w-5 h-5 text-white" />
            ) : (
              <VolumeX className="w-5 h-5 text-[var(--text-secondary)]" />
            )}
          </div>
          <div>
            <h4 className="font-medium text-[var(--text-primary)]">Voice Responses</h4>
            <p className="text-sm text-[var(--text-tertiary)]">
              {voiceEnabled ? 'AIRA will speak responses aloud' : 'Voice responses disabled'}
            </p>
          </div>
        </div>
        <button
          onClick={() => setVoiceEnabled(!voiceEnabled)}
          className={`w-12 h-6 rounded-full p-1 transition-colors ${
            voiceEnabled ? 'bg-[var(--color-primary)]' : 'bg-[var(--bg-tertiary)]'
          }`}
          data-testid="voice-toggle"
        >
          <motion.div
            animate={{ x: voiceEnabled ? 24 : 0 }}
            className="w-4 h-4 rounded-full bg-white"
          />
        </button>
      </div>

      {voiceEnabled && (
        <>
          {/* AIRA VOCAL Status */}
          <div className={`flex items-center gap-3 p-3 rounded-lg ${
            airaVocalAvailable 
              ? 'bg-green-500/10 border border-green-500/20' 
              : 'bg-yellow-500/10 border border-yellow-500/20'
          }`}>
            <Zap className={`w-5 h-5 ${airaVocalAvailable ? 'text-green-400' : 'text-yellow-400'}`} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${airaVocalAvailable ? 'text-green-400' : 'text-yellow-400'}`}>
                {airaVocalAvailable ? 'AIRA VOCAL Active' : 'Using Browser Voice'}
              </p>
              <p className="text-xs text-[var(--text-tertiary)]">
                {airaVocalAvailable 
                  ? 'High-quality server-side voice engine' 
                  : 'Browser TTS fallback active'}
              </p>
            </div>
            {airaVocalLoading && (
              <Loader2 className="w-4 h-4 animate-spin text-[var(--color-primary)]" />
            )}
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 p-1 bg-[var(--bg-card)] rounded-xl">
            {[
              { id: 'personality', label: 'Personality', icon: Sparkles },
              { id: 'voice', label: 'Voice', icon: User },
              { id: 'advanced', label: 'Advanced', icon: Sliders }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm transition-all ${
                  activeTab === tab.id
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)]'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Personality Tab */}
          {activeTab === 'personality' && (
            <div className="space-y-3">
              <label className="block text-sm text-[var(--text-secondary)] font-medium">
                Voice Personality
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.values(VOICE_PERSONALITIES).map((p) => (
                  <motion.button
                    key={p.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => applyPersonality(p.id)}
                    className={`relative p-4 rounded-xl border text-left transition-all ${
                      personality === p.id
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]'
                        : 'border-[var(--border-color)] bg-[var(--bg-card)] hover:border-[var(--border-color-hover)]'
                    }`}
                    data-testid={`personality-${p.id}`}
                  >
                    {personality === p.id && (
                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[var(--color-primary)] flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <h4 className={`font-medium mb-1 ${
                      personality === p.id ? 'text-[var(--color-primary)]' : 'text-[var(--text-primary)]'
                    }`}>
                      {p.name}
                    </h4>
                    <p className="text-xs text-[var(--text-tertiary)]">{p.description}</p>
                  </motion.button>
                ))}
              </div>
            </div>
          )}

          {/* Voice Tab */}
          {activeTab === 'voice' && (
            <div className="space-y-6">
              {/* Gender Selection */}
              <div className="space-y-3">
                <label className="block text-sm text-[var(--text-secondary)] font-medium">
                  Voice Type
                </label>
                <div className="flex gap-3">
                  {Object.values(VOICE_GENDERS).map((g) => (
                    <button
                      key={g.id}
                      onClick={() => setGender(g.id)}
                      className={`flex-1 py-3 px-4 rounded-xl border transition-all ${
                        gender === g.id
                          ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]'
                          : 'border-[var(--border-color)] bg-[var(--bg-card)] hover:border-[var(--border-color-hover)]'
                      }`}
                      data-testid={`gender-${g.id}`}
                    >
                      <span className="text-2xl mb-1 block">{g.icon}</span>
                      <span className={`text-sm ${
                        gender === g.id ? 'text-[var(--color-primary)]' : 'text-[var(--text-primary)]'
                      }`}>
                        {g.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Age Group Selection */}
              {VOICE_AGE_GROUPS && (
                <div className="space-y-3">
                  <label className="block text-sm text-[var(--text-secondary)] font-medium">
                    Voice Age
                  </label>
                  <div className="flex gap-3">
                    {Object.values(VOICE_AGE_GROUPS).map((a) => (
                      <button
                        key={a.id}
                        onClick={() => setAge(a.id)}
                        className={`flex-1 py-2 px-3 rounded-lg border text-sm transition-all ${
                          age === a.id
                            ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                            : 'border-[var(--border-color)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:border-[var(--border-color-hover)]'
                        }`}
                        data-testid={`age-${a.id}`}
                      >
                        {a.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Accent Selection */}
              <div className="space-y-3">
                <label className="block text-sm text-[var(--text-secondary)] font-medium">
                  <Globe className="w-4 h-4 inline mr-2" />
                  Accent Style
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.values(VOICE_ACCENTS).map((a) => (
                    <button
                      key={a.id}
                      onClick={() => setAccent(a.id)}
                      className={`py-2 px-3 rounded-lg border text-sm transition-all ${
                        accent === a.id
                          ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                          : 'border-[var(--border-color)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:border-[var(--border-color-hover)]'
                      }`}
                      data-testid={`accent-${a.id}`}
                    >
                      <span className="mr-2">{a.flag}</span>
                      {a.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Browser Voices (fallback) */}
              {!airaVocalAvailable && availableVoices.length > 0 && (
                <div className="space-y-3">
                  <label className="block text-sm text-[var(--text-secondary)] font-medium">
                    Browser Voice (Fallback)
                  </label>
                  <select
                    value={selectedVoiceIndex}
                    onChange={(e) => setSelectedVoiceIndex(Number(e.target.value))}
                    className="input-field w-full"
                    data-testid="voice-select"
                  >
                    {availableVoices.map((voice, idx) => (
                      <option key={idx} value={idx}>
                        {voice.name} ({voice.lang})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Advanced Tab */}
          {activeTab === 'advanced' && (
            <div className="space-y-6">
              {/* Speed Control */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm text-[var(--text-secondary)] font-medium">
                    Speaking Speed
                  </label>
                  <span className="text-sm text-[var(--color-primary)]">{rate.toFixed(2)}x</span>
                </div>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  className="w-full h-2 bg-[var(--bg-tertiary)] rounded-lg appearance-none cursor-pointer accent-[var(--color-primary)]"
                  data-testid="speed-slider"
                />
                <div className="flex justify-between text-xs text-[var(--text-tertiary)]">
                  <span>Slower</span>
                  <span>Normal</span>
                  <span>Faster</span>
                </div>
              </div>

              {/* Pitch Control */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm text-[var(--text-secondary)] font-medium">
                    Voice Pitch
                  </label>
                  <span className="text-sm text-[var(--color-primary)]">{pitch.toFixed(2)}</span>
                </div>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={pitch}
                  onChange={(e) => setPitch(Number(e.target.value))}
                  className="w-full h-2 bg-[var(--bg-tertiary)] rounded-lg appearance-none cursor-pointer accent-[var(--color-primary)]"
                  data-testid="pitch-slider"
                />
                <div className="flex justify-between text-xs text-[var(--text-tertiary)]">
                  <span>Lower</span>
                  <span>Normal</span>
                  <span>Higher</span>
                </div>
              </div>

              {/* Volume Control */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm text-[var(--text-secondary)] font-medium">
                    Volume
                  </label>
                  <span className="text-sm text-[var(--color-primary)]">{Math.round(volume * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={(e) => setVolume(Number(e.target.value))}
                  className="w-full h-2 bg-[var(--bg-tertiary)] rounded-lg appearance-none cursor-pointer accent-[var(--color-primary)]"
                  data-testid="volume-slider"
                />
              </div>

              {/* Reset to Default */}
              <button
                onClick={() => {
                  setRate(1.0);
                  setPitch(1.0);
                  setVolume(1.0);
                }}
                className="w-full py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
              >
                Reset to Default
              </button>
            </div>
          )}

          {/* Test Voice Button */}
          <div className="pt-4 border-t border-[var(--border-color)]">
            <button
              onClick={isSpeaking ? stopSpeaking : testVoice}
              className={`w-full py-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                isSpeaking
                  ? 'bg-[var(--color-error)] text-white'
                  : 'bg-[var(--color-primary)] text-white hover:opacity-90'
              }`}
              data-testid="test-voice-btn"
            >
              {isSpeaking ? (
                <>
                  <Pause className="w-5 h-5" />
                  Stop Speaking
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Test Voice
                </>
              )}
            </button>
            
            {isSpeaking && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-3 flex items-center gap-2"
              >
                <Mic className="w-4 h-4 text-[var(--color-accent)] animate-pulse" />
                <span className="text-sm text-[var(--text-tertiary)]">AIRA is speaking...</span>
              </motion.div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export { VoiceSettingsPanel };
